import type { ClawbackKind, ClawbackOfficial, ClawbackPreview } from '@/models/tournament';

/**
 * Money in the viewer's locale with the currency's own symbol and decimals. An unknown ISO code makes
 * Intl throw a RangeError, so it falls back to a 2-decimal number followed by the code. Shared by the
 * claw-back consent dialog and the tournament delete preview so both show the same figure the same way.
 */
export function formatCurrencyAmount(amount: number, currencyCode: string | null | undefined, language: string): string {
  const code = (currencyCode ?? '').trim();
  if (code) {
    try {
      return new Intl.NumberFormat(language, { style: 'currency', currency: code }).format(amount);
    } catch {
      // fall through to number + code
    }
  }
  const n = new Intl.NumberFormat(language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  return code ? `${n} ${code}` : n;
}

/** The two kinds HR is told about; anything else the server may add later reads as the safer "next payroll". */
export function clawbackKindOf(kind: string): ClawbackKind {
  return kind === 'pendingEntry' ? 'pendingEntry' : 'nextPayroll';
}

/** Which kinds appear anywhere in the officials' lines, in a stable order (pending first). */
export function clawbackKindsPresent(officials: ClawbackOfficial[]): ClawbackKind[] {
  const seen = new Set<ClawbackKind>();
  for (const o of officials) for (const l of o.lines) seen.add(clawbackKindOf(l.kind));
  return (['pendingEntry', 'nextPayroll'] as ClawbackKind[]).filter((k) => seen.has(k));
}

/** True when the preview actually takes money back (the server returns null otherwise, but be defensive). */
export function hasClawbackMoney(preview: ClawbackPreview | null | undefined): preview is ClawbackPreview {
  return !!preview && (preview.officials?.length ?? 0) > 0;
}
